import {
  COLLECTION_FIELDS,
  CONTEXT_SCOPE_LABELS,
  DomainError,
  FINDING_KIND_LABELS,
  FINDING_KINDS,
  RELATIONSHIP_LABELS,
  acceptFinding,
  contextOptionLabel,
  describeSourceMapping,
  dismissFinding,
  findingHasProposal,
  findingTargetTitle,
  scalarField,
  typedFieldLabel,
  typedSection,
  type Finding,
  type FindingKind,
  type NormalizedBoard,
} from '@blueprint/domain'
import { useMemo, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button, Select } from '../../components/controls.js'
import { useBoardEditable, useBoardStore } from '../../state/board-store.js'
import { useOpenFindingCount } from '../../state/selectors.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './findings.module.css'

/** A finding's proposal as plain lines a person can check before accepting. */
export function describeProposal(state: NormalizedBoard, finding: Finding): string[] {
  const proposal = finding.proposal
  if (!proposal) return []
  const lines: string[] = []
  const patch = proposal.context ?? {}
  const collectionLabel = (key: string) => COLLECTION_FIELDS.find((field) => field.key === key)?.label ?? key
  for (const [key, value] of Object.entries(patch.set ?? {})) {
    const field = scalarField(key as never)
    const shown = field.options ? contextOptionLabel(value) : value
    lines.push(value ? `Set ${field.label.toLowerCase()} to “${shown}”` : `Clear ${field.label.toLowerCase()}, inheriting it again`)
  }
  const listed = (verb: string, part: Partial<Record<string, string[]>> | undefined) => {
    for (const [key, items] of Object.entries(part ?? {})) {
      if (items && items.length > 0) lines.push(`${verb} ${collectionLabel(key).toLowerCase()}: ${items.join(', ')}`)
    }
  }
  listed('Add to', patch.add)
  listed('Remove from', patch.remove)
  listed('Exclude inherited', patch.exclude)
  listed('Restore inherited', patch.restore)
  for (const [sectionKey, fields] of Object.entries(patch.typed ?? {})) {
    const section = typedSection(sectionKey)?.label ?? sectionKey
    for (const [fieldKey, value] of Object.entries(fields)) {
      const label = typedFieldLabel(sectionKey, fieldKey)
      lines.push(value ? `Set ${section} ${label.toLowerCase()} to “${value}”` : `Clear ${section} ${label.toLowerCase()}`)
    }
  }
  const named = (items: (string | { repository?: string; path?: string })[] | undefined) =>
    (items ?? []).map((item) => (typeof item === 'string' ? item : `${item.repository ? `${item.repository}:` : ''}${item.path ?? ''}`))
  if (patch.addSourceMappings?.length) {
    lines.push(`Add source mappings: ${patch.addSourceMappings.map(describeSourceMapping).join(', ')}`)
  }
  if (patch.removeSourceMappings?.length) lines.push(`Remove source mappings: ${named(patch.removeSourceMappings).join(', ')}`)
  if (patch.excludeSourceMappings?.length) lines.push(`Exclude inherited source mappings: ${named(patch.excludeSourceMappings).join(', ')}`)
  if (patch.restoreSourceMappings?.length) lines.push(`Restore inherited source mappings: ${named(patch.restoreSourceMappings).join(', ')}`)
  for (const relationship of proposal.addRelationships ?? []) {
    const target = state.cards[relationship.targetId]?.title ?? 'a card no longer on the board'
    lines.push(`Add relationship: ${RELATIONSHIP_LABELS[relationship.type].toLowerCase()} “${target}”`)
  }
  return lines
}

/** Open findings about one element, as a small marker that opens the panel narrowed to it. */
export function FindingsBadge({ targetId, label }: { targetId: string; label: string }) {
  const count = useOpenFindingCount(targetId)
  if (count === 0) return null
  const text = `${count} open finding${count === 1 ? '' : 's'} on ${label}`
  return (
    <button
      type="button"
      className={styles.badge}
      title={text}
      aria-label={text}
      onClick={(event) => {
        // Never also open the card editor underneath.
        event.stopPropagation()
        useUiStore.getState().setReconciliation({ targetId })
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <span aria-hidden="true">&#9873;</span>
      {count}
    </button>
  )
}

/** One finding: what was observed, where, what it proposes, and — while open — the decision. */
interface FindingItemProps {
  finding: Finding
  entities: NormalizedBoard
  showTarget?: boolean
  /** When set, decisions are unavailable and this says why. */
  blockedReason?: string | undefined
  /** Called after the finding was accepted or dismissed. */
  onDecided?: (() => void) | undefined
}

export function FindingItem({ finding, entities, showTarget = true, blockedReason, onDecided }: FindingItemProps) {
  const editable = useBoardEditable()
  const [error, setError] = useState<{ message: string; details: string[] } | null>(null)
  const targetTitle = findingTargetTitle(entities, finding)
  const proposal = describeProposal(entities, finding)
  const hasProposal = findingHasProposal(finding)

  const decide = (produce: typeof acceptFinding) => {
    setError(null)
    try {
      // Built first so a proposal that no longer fits is explained here, not swallowed.
      const command = produce(entities, finding.id)
      useBoardStore.getState().run(() => command)
      onDecided?.()
    } catch (thrown) {
      if (thrown instanceof DomainError) {
        setError({ message: thrown.message, details: Object.values(thrown.details ?? {}) })
      } else {
        throw thrown
      }
    }
  }

  return (
    <article className={`${styles.finding} ${finding.status !== 'open' ? styles.resolved : ''}`} aria-label={finding.summary}>
      <header className={styles.findingHeader}>
        <span className={`${styles.kind} ${styles[`kind_${finding.kind}`] ?? ''}`}>{FINDING_KIND_LABELS[finding.kind]}</span>
        <strong className={styles.summary}>{finding.summary}</strong>
        {finding.status !== 'open' ? <span className={styles.status}>{finding.status}</span> : null}
      </header>
      <p className={styles.meta}>
        {showTarget ? (
          <>
            {CONTEXT_SCOPE_LABELS[finding.targetScope]}:{' '}
            {targetTitle ?? <em>no longer on the board</em>}
            {' · '}
          </>
        ) : null}
        {finding.reporter ? `Reported by ${finding.reporter} · ` : ''}
        {new Date(finding.createdAt).toLocaleString()}
      </p>
      {finding.detail ? <p className={styles.detail}>{finding.detail}</p> : null}
      {finding.evidence.length > 0 ? (
        <ul className={styles.evidence} aria-label="Evidence">
          {finding.evidence.map((mapping, index) => (
            <li key={index}>
              <code>{describeSourceMapping(mapping)}</code>
            </li>
          ))}
        </ul>
      ) : null}
      {proposal.length > 0 ? (
        <div className={styles.proposal}>
          <p className={styles.proposalTitle}>Proposed change</p>
          <ul>
            {proposal.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {error ? (
        <div role="alert" className={styles.error}>
          {error.message}
          {error.details.length > 0 ? (
            <ul>
              {error.details.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {finding.status === 'open' && editable ? (
        <div className={styles.actions}>
          {blockedReason ? <span className={styles.meta}>{blockedReason}</span> : null}
          <Button disabled={Boolean(blockedReason)} onClick={() => decide(dismissFinding)}>
            Dismiss
          </Button>
          <Button
            variant="primary"
            disabled={Boolean(blockedReason) || (targetTitle === null && hasProposal)}
            onClick={() => decide(acceptFinding)}
          >
            {hasProposal ? 'Accept proposal' : 'Acknowledge'}
          </Button>
        </div>
      ) : null}
    </article>
  )
}

type StatusFilter = 'open' | 'accepted' | 'dismissed' | 'all'

/** Every finding on the board, filtered, with accept and dismiss. Only people decide here. */
export function ReconciliationDialog() {
  const reconciliation = useUiStore((state) => state.reconciliation)
  const setReconciliation = useUiStore((state) => state.setReconciliation)
  const entities = useBoardStore((state) => state.entities)
  const [status, setStatus] = useState<StatusFilter>('open')
  const [kind, setKind] = useState<FindingKind | ''>('')

  const targetId = reconciliation?.targetId ?? null
  const findings = useMemo(() => {
    if (!entities) return []
    return Object.values(entities.findings ?? {})
      .filter((finding) => (status === 'all' ? true : finding.status === status))
      .filter((finding) => (kind ? finding.kind === kind : true))
      .filter((finding) => (targetId ? finding.targetId === targetId : true))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id))
  }, [entities, status, kind, targetId])

  if (!reconciliation || !entities) return null
  const targetFinding = targetId ? Object.values(entities.findings ?? {}).find((finding) => finding.targetId === targetId) : undefined
  const targetTitle = targetFinding ? findingTargetTitle(entities, targetFinding) : null

  return (
    <Dialog
      open
      xwide
      title="Reconciliation"
      onClose={() => setReconciliation(null)}
      footer={<Button onClick={() => setReconciliation(null)}>Close</Button>}
    >
      <p className={styles.intro}>
        What agents observed in the implementation. Findings never change the intended architecture by
        themselves: accepting a proposal applies it as an ordinary, undoable edit; dismissing keeps the record.
      </p>
      <div className={styles.filters}>
        <label>
          Status{' '}
          <Select aria-label="Finding status" value={status} onChange={(event) => setStatus(event.target.value as StatusFilter)}>
            <option value="open">Open</option>
            <option value="accepted">Accepted</option>
            <option value="dismissed">Dismissed</option>
            <option value="all">All</option>
          </Select>
        </label>
        <label>
          Kind{' '}
          <Select aria-label="Finding kind" value={kind} onChange={(event) => setKind(event.target.value as FindingKind | '')}>
            <option value="">All kinds</option>
            {FINDING_KINDS.map((value) => (
              <option key={value} value={value}>
                {FINDING_KIND_LABELS[value]}
              </option>
            ))}
          </Select>
        </label>
        {targetId ? (
          <span className={styles.targetFilter}>
            Only about {targetTitle ? `“${targetTitle}”` : 'one element'}
            <button type="button" className={styles.linkButton} onClick={() => setReconciliation({ targetId: null })}>
              Show all
            </button>
          </span>
        ) : null}
      </div>
      {findings.length === 0 ? (
        <p className={styles.empty}>No findings match.</p>
      ) : (
        <div className={styles.list}>
          {findings.map((finding) => (
            <FindingItem key={finding.id} finding={finding} entities={entities} />
          ))}
        </div>
      )}
    </Dialog>
  )
}
